import React, {
  useMemo,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import * as yup from "yup";
import UIkit from "uikit";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";
import GoogleAutoComplete from "../GoogleAutoComplete";
import { AppContexts } from "../../../providers";

const schema = yup.object().shape({
  pms_id: yup.string().required(),
});

// Shared form modal for creating/editing rentals (units and property guides)
const RentalFormModal = ({ config = {} }) => {
  const {
    showDestinationField = false,
    showLatLng = false,
    showGuideName = false,
    title = "Add Rental",
    defaultDestinationId = 0,
    showLogs = false,
    redirectPath = "/rentals",
    messageOnAdd = "Added Successfully",
    messageOnEdit = "Updated Successfully",
  } = config;

  const history = useHistory();
  const [placeId, setPlaceId] = useState(null);
  const UtilContext = useContext(AppContexts.UtilContext);
  const { user } = useContext(AppContexts.AuthContext);
  const RentalsContext = useContext(AppContexts.RentalsContext);
  const DestinationContext = useContext(AppContexts.DestinationContext);
  const [isValidate, setValidate] = useState(true);
  const [autoCompleteValue, setAutoCompleteValue] = useState("");
  const offcanvasRef = useRef(null);

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    reset,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const listDestinations = useMemo(() => {
    if (!DestinationContext?.listDestinations) {
      return [];
    }
    return DestinationContext.listDestinations;
  }, [DestinationContext?.listDestinations]);

  const handleAddressSelect = async (placeId) => {
    if (placeId && showLatLng) {
      try {
        const place = await UtilContext.place(placeId);
        if (place && place.geometry) {
          setValue("lat", place.geometry.location.lat);
          setValue("lng", place.geometry.location.lng);
        }
      } catch (err) {
        console.log("Error fetching place details:", err);
      }
    }
  };

  const handleRegister = async (data) => {
    placeId ? setValidate(true) : setValidate(false);
    if (placeId) {
      try {
        let rental = {};
        if (RentalsContext.isNew) {
          let place = await UtilContext.place(placeId);
          rental = {
            destination_id: showDestinationField 
              ? (data.destination_id == "" ? 0 : data.destination_id) 
              : defaultDestinationId,
            google_place_id: place.place_id,
            host_id: user.account_id,
            pms_id: data.pms_id || "",
            name: data.name || place.name,
            address: place.formatted_address,
            lat: data.lat || place.geometry.location.lat,
            lng: data.lng || place.geometry.location.lng,
            property_code: "",
          };
          rental = await RentalsContext.createRental(rental);
        } else {
          rental = {
            destination_id: showDestinationField
              ? (data.destination_id == "" ? 0 : data.destination_id)
              : defaultDestinationId,
            host_id: user.account_id,
            pms_id: data.pms_id || "",
            name: data.name,
            property_code: "",
            lat: data.lat,
            lng: data.lng,
          };
          if (placeId !== RentalsContext.rental.google_place_id) {
            let place = await UtilContext.place(placeId);
            rental = {
              destination_id: showDestinationField
                ? (data.destination_id == "" ? 0 : data.destination_id)
                : defaultDestinationId,
              google_place_id: place.place_id,
              host_id: user.account_id,
              pms_id: data.pms_id || "",
              name: data.name || place.name,
              address: place.formatted_address,
              lat: data.lat || place.geometry.location.lat,
              lng: data.lng || place.geometry.location.lng,
              property_code: "",
            };
          }
          rental = await RentalsContext.updateRental(
            RentalsContext.rental.rental_id,
            {
              ...RentalsContext.rental,
              ...rental,
            }
          );
        }

        if (!rental) {
          UIkit.notification("Failed", "error");
          RentalsContext.setUpsertLoading(false);
        } else {
          UIkit.modal("#addPropertyGuide-modal").hide();
          UIkit.notification(
            RentalsContext.isNew ? messageOnAdd : messageOnEdit,
            "success"
          );

          const wasNew = RentalsContext.isNew;
          RentalsContext.get();
          RentalsContext.setIsNew(false);

          if (wasNew && redirectPath) {
            // Replace {id} placeholder with rental_id if present
            const path = redirectPath.includes("{id}") 
              ? redirectPath.replace("{id}", rental.rental_id)
              : redirectPath;
            history.push(path);
          }
        }
      } catch (err) {
	      RentalsContext.setUpsertLoading(false);
        console.log("error: ", err);
      }
    }
  };

  const handleBeforeHide = (e) => {
    if (e.target === offcanvasRef.current) {
      setAutoCompleteValue("");
      setValidate(true);
      reset();
      clearErrors();
      RentalsContext.setRental({});
    }
  };

  useEffect(() => {
    const offcanvasElement = UIkit.offcanvas(offcanvasRef.current);
    const offcanvasDOMElement = offcanvasElement.$el;
    offcanvasDOMElement.addEventListener("beforehide", handleBeforeHide);
    return () => {
      offcanvasDOMElement.removeEventListener("beforehide", handleBeforeHide);
    };
  }, []);

  useEffect(() => {
    if (RentalsContext.isNew) {
      reset();
      setAutoCompleteValue("");
    } else {
      setValue("pms_id", RentalsContext.rental.pms_id);
      setValue("name", RentalsContext.rental.name);
      if (showDestinationField) {
        setValue("destination_id", RentalsContext.rental.destination_id);
      }
      setPlaceId(RentalsContext.rental.google_place_id);
      setAutoCompleteValue(RentalsContext.rental.address);
      setValue("lat", RentalsContext.rental.lat);
      setValue("lng", RentalsContext.rental.lng);
      if (showLogs) {
        RentalsContext.getLogs(RentalsContext.rental.rental_id);
      }
    }
  }, [RentalsContext.isNew, RentalsContext.rental]);

  useEffect(() => {
    if (RentalsContext.error)
      setError("pms_id", { type: "custom", message: RentalsContext.error });
  }, [RentalsContext.error]);

  useEffect(() => {
    RentalsContext.setPMSError(null);
  }, [watch("pms_id")]);

  const destination_id = useMemo(
    () => getValues("destination_id"),
    [watch("destination_id")]
  );

  const handleError = (err) => {
    console.log(err);
  };

  return (
    <div
        id="addPropertyGuide-modal"
        className="uk-modal-full hms-modals uk-modal"
        uk-offcanvas="overlay: true; flip: true;"
        ref={offcanvasRef}
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close=""
          ></button>
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">
              {RentalsContext.isNew ? "Add" : "Edit"} {title}
            </h2>
          </div>
          <div className="uk-modal-body">
            <form
              className="uk-grid-small"
              uk-grid=""
              onSubmit={handleSubmit(handleRegister, handleError)}
            >
              <p className="secondary">
                Your subscription is based on the number of units. 
                Adding a unit will increase your subscription cost.
              </p>
              <div className="uk-width-1-1">
                <div className="uk-form-controls">
                  <label className="uk-form-label">Property Address*</label>
                  <GoogleAutoComplete
                    className={classnames({
                      error: isSubmitted && (!isValidate || !autoCompleteValue),
                    })}
                    width="100%"
                    onSelect={(_, value) => {
                      setPlaceId(value.text);
                      handleAddressSelect(value.text);
                    }}
                    onChange={(e) => setAutoCompleteValue(e)}
                    address={autoCompleteValue}
                  />
                </div>
                <div className="hms-small-itelic">
                  Begin typing and select a suggested address.
                </div>
              </div>

              <div className="uk-width-1-1">
                <div className="uk-form-controls">
                  <label className="uk-form-label">
                    Property Id*{" "}
                    <img
                      className="info-icon"
                      src="/images/info-icon.svg"
                      uk-tooltip="Use the ID assigned in your property management software."
                    />
                  </label>
                  <input
                    className={classnames("uk-input", {
                      error: errors["pms_id"],
                    })}
                    type="text"
                    {...register("pms_id")}
                  />
                  <div className="uk-label-danger">
                    {RentalsContext.error}
                  </div>
                </div>
              </div>

              {showGuideName && (
                <div className="uk-width-1-1">
                  <div className="uk-form-controls">
                    <label className="uk-form-label">
                      Guide Name{" "}
                      <img
                        className="info-icon"
                        src="/images/info-icon.svg"
                        uk-tooltip="Title of the home guide, visibile to guests."
                      />
                    </label>
                    <input
                      className={classnames("uk-input", {
                        error: errors["name"],
                      })}
                      type="text"
                      {...register("name")}
                    />
                  </div>
                </div>
              )}

              {showDestinationField && (
                <div className="uk-width-1-1">
                  <div uk-form-custom="target: > * > span:first-child">
                    <label className="uk-form-label">Area Guide</label>
                    <select {...register("destination_id")}>
                      <option value="">
                        Select a area guide for this property
                      </option>
                      {listDestinations.map((destination_guide, index) => (
                        <option
                          value={destination_guide.destination_id}
                          key={index}
                        >
                          {destination_guide.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="uk-button uk-button-default"
                      type="button"
                      tabIndex="-1"
                    >
                      <span>
                        {!!listDestinations.find(
                          (item) => item.destination_id === destination_id
                        )
                          ? listDestinations.find(
                              (item) =>
                                item.destination_id === destination_id
                            ).name
                          : "Select a area guide for this property"}
                      </span>
                      <span
                        uk-icon="icon: chevron-down"
                        className="hms-align-right"
                      ></span>
                    </button>
                  </div>
                </div>
              )}

              {showLatLng && (
                <>
                  <div className="uk-width-1-2@s">
                    <div className="uk-form-controls">
                      <label className="uk-form-label">Latitude{RentalsContext.isNew && " (Auto-populated)"}</label>
                      <input
                        className={classnames("uk-input", {
                          error: errors["lat"],
                        })}
                        type="text"
                        {...register("lat")}
                      />
                    </div>
                  </div>
                  <div className="uk-width-1-2@s">
                    <div className="uk-form-controls">
                      <label className="uk-form-label">Longitude{RentalsContext.isNew && " (Auto-populated)"}</label>
                      <input
                        className={classnames("uk-input", {
                          error: errors["lng"],
                        })}
                        type="text"
                        {...register("lng")}
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <button
                  className="uk-button hms-btn hms-red-btn"
                  type="submit"
                  disabled={RentalsContext.upsertLoading}
                >
                  {RentalsContext.upsertLoading ? (
                    <div data-uk-spinner="ratio: 0.5;">Saving </div>
                  ) : (
                    "Continue"
                  )}
                </button>
                <button
                  className="uk-button hms-btn uk-offcanvas-close"
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalFormModal;
